import { MatrixClient } from 'matrix-js-sdk/src/matrix';

import { createMeeting } from 'api/meedioBackend/createMeeting';
import { CreateMeetingRequest, MeetingResponse } from 'api/meedioBackend/types';
import logger from 'utils/logging/faro';

export type RegisterInstantMeetingParams = {
  matrixClient: MatrixClient;
  /** The already-created instant-call space whose IS meeting we register. */
  spaceRoomId: string;
  /** Human-readable title used for the IS `room_name`. */
  meetingTitle: string;
  /** Caller-specific `createMeeting` fields (messenger_room_id, add_guest_mxids, is_instant, …). */
  extraMeetingFields?: Partial<CreateMeetingRequest>;
  /** Identifies the calling path in non-fatal warn logs. */
  logLabel: string;
};

/**
 * Shared instant-call core used by both the Video Hub (`InstantCallModal`) and the messenger
 * (`useCreateMessengerCall`) paths. Registers the meeting with the IS using the Matrix room ID.
 *
 * Instant meetings deliberately carry **no alias** (#8263): an auto-generated `slug-NNNN` alias
 * caps the app at ~10,000 instant meetings before the 4-digit postfix space is exhausted. The
 * room is addressed by its Matrix room ID instead, so URLs and invite links use `matrix_room_id`.
 *
 * IS registration is non-fatal: a transient IS failure must not abort an already-created meeting.
 */
export const registerInstantMeeting = async ({
  spaceRoomId,
  meetingTitle,
  extraMeetingFields,
  logLabel,
}: RegisterInstantMeetingParams): Promise<MeetingResponse | undefined> =>
  createMeeting({
    matrix_room_id: spaceRoomId,
    ...(meetingTitle ? { room_name: meetingTitle } : {}),
    ...extraMeetingFields,
  }).catch((err: unknown) => {
    logger.warn(`${logLabel}: failed to register instant call with IS`, { error: err });
    return undefined;
  });
